import { Suspense, lazy, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Link } from "@mui/material";
import { TheatersRounded, WhatshotRounded } from "@mui/icons-material";
import Seo, { SITE_URL } from "src/components/Seo.js";
import StatusSnackbar from "src/components/blocks/StatusSnackbar.js";
import { getPageRoute } from "src/services";
import {
  CALENDARS_PER_SECTION,
  DESKTOP_CALENDARS_PER_SECTION,
  useSectionCalendars,
  useSections,
} from "src/services";
import { AuthContext } from "src/utils/AuthContext.js";
import { customEvent } from "src/utils/gtag.js";
import { useDimensions } from "src/utils/useDimensions.js";
import { ErrorBoundary } from "src/utils/ErrorBoundary/index.js";
import { isBeforeElectionDay } from "src/components/elections/electionDates.js";
import { useIsUsRegion } from "src/utils/useIsUsRegion.js";
import DiscoveryHeroBanner from "../DiscoveryHeroBanner.js";
import LeagueLeaderboardRail from "../LeagueLeaderboardRail.js";
import DeferredMount from "../DeferredMount.js";
import LiveTodayHero, { countdownState } from "../LiveTodayHero.js";
import LogoCircleRail from "../LogoCircleRail.js";
import { hasSportsTag } from "@stanza/shared/sports-tags";
import {
  COLLEGE_CONFERENCE_CIRCLES,
  LEAGUE_CIRCLES,
  STREAMING_CIRCLES,
  TV_NETWORK_CIRCLES,
} from "../logoRails.js";
import ProductPosterRail from "../ProductPosterRail.js";
import RequestCalendarBand from "../RequestCalendarBand.js";
import SectionRail from "../SectionRail.js";
import StanzaPlusBand from "../StanzaPlusBand.js";
import { roundRobinPools, toTileCalendar } from "../calendarTileUtils.js";
import { HOME_SEO_META, buildHomeJsonLd } from "@stanza/shared/seo/home-page";

// Plain React.lazy under a local boundary: if the chunk fails, the band just
// doesn't render, instead of reloading or replacing the home page.
const ElectionsBand = lazy(() => import("../ElectionsBand.js"));

/** Placeholder height for the deferred elections band. */
const ELECTIONS_BAND_MIN_HEIGHT = 360;

/** Sports leagues sourcing the live-today carousel until its endpoint lands. */
const HERO_LEAGUE_TAGS = ["nfl", "nba", "mlb", "nhl", "premier-league"];

/** The sports zone's two leaderboards, interleaved with its two section rails. */
const SPORTS_LEADERBOARDS = [
  { tag: "nfl", title: "Most Popular in the NFL" },
  { tag: "nba", title: "Most Popular in the NBA" },
];

/** The "How Stanza works" product cards (trust band between sports and TV). */
const VALUE_PROP_ITEMS = [
  { slug: "how-stanza-gets-schedules", blurb: "Official feeds and creators, always sourced." },
  { slug: "transparent-updates", blurb: "See when each calendar last updated." },
  { slug: "groups", blurb: "Every team and show in one calendar." },
  { slug: "how-sports-fans-use-stanza", blurb: "Never miss a kickoff or a release." },
];

/** Rails per category zone. */
const ZONE_SECTION_COUNT = 4;

/** Calendars per hero pool. */
const HERO_POOL_SIZE = 8;

/**
 * Whether a calendar belongs in a medium-scoped hero pool. Mixed-media genre
 * sections carry both films and shows; calendars without tags pass (fail-open).
 *
 * @param {object} calendar
 * @param {"movies"|"tv"|undefined} medium
 * @returns {boolean}
 */
function matchesMedium(calendar, medium) {
  if (!medium || !calendar.tags?.length) return true;
  const isMovie = calendar.tags.includes("movies");
  return medium === "movies" ? isMovie : !isMovie;
}

/**
 * Builds a hero pool by round-robining one calendar per section, so the first
 * section (one genre) can't monopolize the cap. Only calendars with a live,
 * today, or upcoming featured event qualify; deduped by calendarId.
 *
 * @param {Array<object>} sections
 * @param {{ medium?: "movies"|"tv" }} [options]
 * @returns {Array<object>}
 */
function diverseHeroPool(sections, { medium } = {}) {
  const now = new Date();
  const pools = sections.map((section) =>
    (section.calendars ?? [])
      .filter(
        (calendar) =>
          matchesMedium(calendar, medium) &&
          countdownState(calendar.mostRecentEvent, now) !== null,
      )
      .sort((a, b) => (b.trending?.score ?? 50) - (a.trending?.score ?? 50)),
  );
  return roundRobinPools(pools, HERO_POOL_SIZE);
}

/**
 * Discovery home, laid out as curated zones: the hero banner, a live-today
 * carousel, the signed-in user's personalized rails, sports (league circles +
 * leaderboard/section interleave), the "How Stanza works" trust band, TV
 * (streaming + network circles and rails), movies (upcoming carousel + rails),
 * and a request-a-calendar CTA.
 */
const DiscoveryHome = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { isMediumScreen } = useDimensions();
  const isUs = useIsUsRegion();
  // A dead calendar link (deleted creator, stale /event/:id URL) redirects here
  // with `calendarNotFound` state; surface it instead of a silent bounce.
  const [notFoundOpen, setNotFoundOpen] = useState(false);
  useEffect(() => {
    if (location.state?.calendarNotFound) setNotFoundOpen(true);
  }, [location.state?.calendarNotFound]);
  const calendarsPerSection = isMediumScreen
    ? CALENDARS_PER_SECTION
    : DESKTOP_CALENDARS_PER_SECTION;

  // Page 1 of the unfiltered feed carries the backend-prepended personalized
  // rails for signed-in users; the curated zones below replace the rest of it.
  // Anonymous visitors can't have personalized rails, so skip the request.
  const { sections: feedSections } = useSections(
    { pageSize: ZONE_SECTION_COUNT, calendarsPerSection },
    { enabled: Boolean(user) },
  );
  const personalizedSections = useMemo(
    () => feedSections.filter((section) => section.source === "personalized"),
    [feedSections],
  );

  // Timely mixes sports/TV/movie weeklies; the sports zone keeps only the
  // league-tagged ones.
  const { sections: timelySections } = useSections({
    category: "timely",
    pageSize: 6,
    calendarsPerSection,
  });
  const sportsSections = useMemo(
    () =>
      timelySections
        .filter((section) => hasSportsTag(section.tags))
        .slice(0, SPORTS_LEADERBOARDS.length),
    [timelySections],
  );
  // Trending sports rail: the trend-sports shelf's members in stored
  // (trend-score) order; takes the second sports slot.
  const { calendars: trendingSportsCalendars } = useSectionCalendars("trend-sports", { pageSize: 20 });
  const trendingSportsSection = useMemo(
    () => ({
      slug: "trend-sports",
      title: "Trending in Sports",
      source: "algorithmic",
      category: "sports",
      calendars: trendingSportsCalendars,
    }),
    [trendingSportsCalendars],
  );
  // Hero sports slot: same trending shelf, capped for pool balance. liveFirst
  // (the pool default) still floats live games to the front.
  const sportsHeroCalendars = useMemo(
    () => trendingSportsCalendars.slice(0, HERO_POOL_SIZE),
    [trendingSportsCalendars],
  );
  const { sections: tvSections } = useSections({
    category: "tv",
    pageSize: ZONE_SECTION_COUNT,
    calendarsPerSection,
  });
  const { sections: movieSections } = useSections({
    category: "movies",
    pageSize: ZONE_SECTION_COUNT,
    calendarsPerSection,
  });
  // Trending TV hero: the trending-tv shelf's members in stored (trend-score)
  // order; rendered as its own carousel, so keep it out of the TV rails.
  const { calendars: trendingTvCalendars } = useSectionCalendars("trend-tv", { pageSize: 20 });
  const tvRailSections = useMemo(
    () => tvSections.filter((section) => section.slug !== "trend-tv"),
    [tvSections],
  );

  const tvHeroCalendars = useMemo(
    () => diverseHeroPool(tvSections, { medium: "tv" }),
    [tvSections],
  );
  // Trending movies hero: the trend-movies shelf's members in stored
  // (trend-score) order; rendered as its own carousel, so keep it out of the
  // movie rails.
  const { calendars: trendingMovieCalendars } = useSectionCalendars("trend-movies", { pageSize: 20 });
  const movieRailSections = useMemo(
    () => movieSections.filter((section) => section.slug !== "trend-movies"),
    [movieSections],
  );
  // Hero movie slot: cap the trending pool so the interleaved carousel keeps
  // its sports/TV/movie balance instead of ending in a movies-only tail.
  const movieHeroCalendars = useMemo(
    () => trendingMovieCalendars.slice(0, HERO_POOL_SIZE),
    [trendingMovieCalendars],
  );

  useEffect(() => {
    customEvent({
      name: "landed_on_home_page",
      category: "Navigation",
      label: "Landed on Home Page",
      data: {
        ...(user ? { user_id: user._id, user_email: user.email } : {}),
      },
    });
  }, [user?._id, user?.email]);

  return (
    <>
      <Seo
        title={HOME_SEO_META.title}
        description={HOME_SEO_META.description}
        jsonLd={buildHomeJsonLd(SITE_URL)}
      />
      <DiscoveryHeroBanner />

      <LiveTodayHero
        title="Trending"
        titleIcon={<WhatshotRounded sx={{ fontSize: 26 }} />}
        pools={[
          { key: "sports", calendars: sportsHeroCalendars },
          { key: "tv", liveFirst: false, calendars: tvHeroCalendars },
          { key: "movies", icon: TheatersRounded, liveFirst: false, preserveOrder: true, calendars: movieHeroCalendars },
        ]}
        section={{ slug: "live-today", title: "Live today", source: "home" }}
      />

      {/* The first personalized rail lands eagerly with the hero; everything
          below defers its mount until it nears the viewport, so load-time
          cost covers the first screens rather than the whole feed. */}
      {personalizedSections.map((section, index) =>
        index === 0 ? (
          <SectionRail key={`${section.source}-${section.slug}`} section={section} />
        ) : (
          <DeferredMount key={`${section.source}-${section.slug}`}>
            <SectionRail section={section} />
          </DeferredMount>
        ),
      )}

      {isUs && isBeforeElectionDay() && (
        <DeferredMount minHeight={ELECTIONS_BAND_MIN_HEIGHT}>
          <ErrorBoundary fallback={null}>
            <Suspense fallback={<Box sx={{ minHeight: ELECTIONS_BAND_MIN_HEIGHT }} />}>
              <ElectionsBand />
            </Suspense>
          </ErrorBoundary>
        </DeferredMount>
      )}

      <LogoCircleRail title="Major Leagues" items={LEAGUE_CIRCLES} />
      <LogoCircleRail title="College Conferences" items={COLLEGE_CONFERENCE_CIRCLES} />
      {SPORTS_LEADERBOARDS.map((rail, index) => (
        <DeferredMount
          key={rail.tag}
          minHeight={
            (index === 1 ? trendingSportsSection.calendars.length > 0 : sportsSections[index]) ? 700 : 360
          }
        >
          <LeagueLeaderboardRail
            tag={rail.tag}
            title={rail.title}
            section={{ slug: `sports-${rail.tag}`, title: rail.title, source: "home" }}
          />
          {index === 1
            ? trendingSportsSection.calendars.length > 0 && (
                <SectionRail
                  key={`${trendingSportsSection.source}-${trendingSportsSection.slug}`}
                  section={trendingSportsSection}
                />
              )
            : sportsSections[index] && (
                <SectionRail
                  key={`${sportsSections[index].source}-${sportsSections[index].slug}`}
                  section={sportsSections[index]}
                />
              )}
        </DeferredMount>
      ))}

      <DeferredMount minHeight={420}>
        <ProductPosterRail heading="How Stanza works" items={VALUE_PROP_ITEMS} surface="home" compact />
      </DeferredMount>

      <StanzaPlusBand variant="dark" page="home" />

      {trendingTvCalendars.length > 0 && (
        <DeferredMount minHeight={430}>
          <LiveTodayHero
            title="Trending in TV"
            titleIcon={<WhatshotRounded sx={{ fontSize: 26 }} />}
            pools={[{ key: "tv", calendars: trendingTvCalendars }]}
            section={{ slug: "trend-tv", title: "Trending in TV", source: "home" }}
          />
        </DeferredMount>
      )}
      <LogoCircleRail title="Streaming Platforms" items={STREAMING_CIRCLES} />
      <LogoCircleRail title="TV Networks" items={TV_NETWORK_CIRCLES} />
      {tvRailSections.map((section) => (
        <DeferredMount key={`${section.source}-${section.slug}`} minHeight={420}>
          <SectionRail section={section} />
        </DeferredMount>
      ))}

      {trendingMovieCalendars.length > 0 && (
        <DeferredMount minHeight={430}>
          <LiveTodayHero
            title="In Theaters Soon"
            titleIcon={<TheatersRounded sx={{ fontSize: 26 }} />}
            pools={[
              {
                key: "movies",
                icon: TheatersRounded,
                liveFirst: false,
                preserveOrder: true,
                calendars: trendingMovieCalendars,
              },
            ]}
            section={{ slug: "trend-movies", title: "In theaters soon", source: "home" }}
          />
        </DeferredMount>
      )}
      {movieRailSections.map((section) => (
        <DeferredMount key={`${section.source}-${section.slug}`} minHeight={420}>
          <SectionRail section={section} />
        </DeferredMount>
      ))}

      <RequestCalendarBand />

      <StatusSnackbar
        openState={notFoundOpen}
        setSnackbarOpen={setNotFoundOpen}
        severity="error"
        message={
          <>
            This calendar wasn&apos;t found. Try{" "}
            <Link
              component="span"
              sx={{ cursor: "pointer" }}
              onClick={() => navigate(getPageRoute("chat", "SEARCH"))}
            >
              searching
            </Link>{" "}
            for it.
          </>
        }
      />
    </>
  );
};

export default DiscoveryHome;
